import axios from 'axios';

const ACTIVE_LIMIT = 50;

async function fetchByStatus(status) {
  const { data } = await axios.get('/api/v1/workflows/history', {
    params: {
      status,
      limit: ACTIVE_LIMIT,
      offset: 0,
      sort_by: 'created_at',
      sort_order: 'desc',
      include_cost_breakdown: false,
    },
  });
  // v1 endpoint returns { workflows: [...], total_count, ... }
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.workflows)) return data.workflows;
  return [];
}

// The v1 AI workflow history endpoint accepts a single status, validated
// against: draft, ready, executing, completed, failed, cancelled,
// payment_required, paused. In-flight work = executing + paused. Issue both
// in parallel and merge. We normalise status='executing' to 'running' on the
// way out so the rest of the UI (and the dropdown) can speak one language.
//
// Dedupe by workflow_id: a workflow can only have one true status at a time,
// but a paused-and-needs-input flow occasionally surfaces in both queries
// (race during status transition, or fuzzy server-side filtering). When a
// duplicate appears, prefer paused — that's the more actionable state.
export async function fetchActiveWorkflows() {
  const [executing, paused] = await Promise.all([
    fetchByStatus('executing'),
    fetchByStatus('paused'),
  ]);
  const normalised = [
    // Paused first so it wins the dedupe.
    ...paused.map((w) => ({ ...w, status: 'paused' })),
    ...executing.map((w) => ({ ...w, status: 'running' })),
  ];
  const seen = new Map();
  for (const w of normalised) {
    if (!seen.has(w.workflow_id)) seen.set(w.workflow_id, w);
  }
  return [...seen.values()];
}

// For a paused workflow we need to know whether any node is waiting_approval to
// surface "needs input". The history payload is workflow-level only; fetch the
// full detail for paused ones.
export async function fetchWorkflowDetail(workflowId) {
  const { data } = await axios.get(`/api/v1/workflows/${workflowId}`);
  return data;
}

export function isWaitingApproval(workflowDetail) {
  if (!workflowDetail) return false;
  const nodes = workflowDetail.nodes || workflowDetail.graph?.nodes || [];
  return nodes.some((n) => {
    const status = n?.status || n?.data?.status;
    return status === 'waiting_approval';
  });
}
